import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { Copy, DownloadSimple, ShieldCheck } from '@phosphor-icons/react'

/**
 * One-time display of recovery codes. Codes are shown ONLY here (right after
 * enrollment / regeneration) and never fetched again. The user must confirm they
 * saved them before continuing.
 */
export function RecoveryCodesPanel({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation('auth')
  const [acknowledged, setAcknowledged] = useState(false)

  const copyAll = () => {
    void navigator.clipboard?.writeText(codes.join('\n'))
    toast.success(t('twoFactor.enroll.copiedCodes'))
  }

  const download = () => {
    const blob = new Blob([`PinDay recovery codes\n\n${codes.join('\n')}\n`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'pinday-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck size={18} /> {t('twoFactor.enroll.recoveryTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('twoFactor.enroll.recoveryHint')}</p>
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-[var(--pd-border)] bg-[var(--pd-muted)] p-4">
          {codes.map((c) => (
            <code key={c} className="break-all text-center font-mono text-sm tracking-wide">
              {c}
            </code>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={copyAll}>
            <Copy size={16} className="mr-2" /> {t('twoFactor.enroll.copyCodes')}
          </Button>
          <Button variant="outline" size="sm" onClick={download}>
            <DownloadSimple size={16} className="mr-2" /> {t('twoFactor.enroll.downloadCodes')}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="rounded border-[var(--pd-border)]"
          />
          {t('twoFactor.enroll.savedConfirm')}
        </label>
        <div className="flex justify-end">
          <Button onClick={onDone} disabled={!acknowledged}>
            {t('twoFactor.enroll.finish')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
